"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RtpayDeviceMessage = exports.RtpayServerMessage = exports.RTPAY_PROTOCOL_VERSION = exports.RtpayResultSchema = exports.RTPAY_TERMINAL_STATUSES = exports.RtpaySaleStatusSchema = exports.RtpayDeviceStateSchema = exports.RtpaySettingsSchema = exports.RtpayReceiptSettingsSchema = exports.RtpayTipSettingsSchema = exports.RTPAY_REGISTER_MODES = exports.RtpayModeSchema = exports.RtpayCardEngineSchema = exports.ANDROID_TAP_TO_PAY_MODEL = exports.RTPAY_EMV_MODE = exports.RTPAY_CREDIT_METHOD = exports.RTPAY_DEVICE_CLASS = void 0;
const zod_1 = require("zod");
/**
 * RUBYTILL PAY — our payment app on a Dejavoo P-series terminal.
 * Spec: docs/agenticpos-rubytill/rubytill-pay.md
 *
 * One definition of the per-device settings and the relay status words, shared by
 * pos-pay-devices-service, super-admin-ext3, super-admin-ui and the POS till lane.
 * The Android app mirrors these in Kotlin (app.rubytill.pay.data.DeviceConfig).
 */
exports.RTPAY_DEVICE_CLASS = 'dejavoo_rubytill_pay';
exports.RTPAY_CREDIT_METHOD = 'credit_dejavoo_rtpay';
exports.RTPAY_EMV_MODE = 'rtpay';
/**
 * ANDROID TAP TO PAY — the same RubyTill Pay route (device class, tender, emvMode,
 * pairing, relay) on a plain Android phone/tablet. Only the card engine differs:
 * Dejavoo's Tap to Pay on Android SDK (NFC tap, credit only, TSYS) instead of
 * DvPayLite. The SDK registers with the TPN plus a Merchant Code.
 */
exports.ANDROID_TAP_TO_PAY_MODEL = 'Android Tap to Pay';
/** What reads the card on the device. `dvpaylite` = P-series terminal (default). */
exports.RtpayCardEngineSchema = zod_1.z.enum(['dvpaylite', 'tap_to_pay']);
/** What the terminal does. Only the first two can be bound to a register. */
exports.RtpayModeSchema = zod_1.z.enum(['charge_only', 'customer_display', 'standalone']);
exports.RTPAY_REGISTER_MODES = ['charge_only', 'customer_display'];
exports.RtpayTipSettingsSchema = zod_1.z.object({
    enabled: zod_1.z.boolean().default(true),
    percents: zod_1.z.array(zod_1.z.number().int().min(1).max(100)).max(4).default([15, 18, 20]),
    allowCustom: zod_1.z.boolean().default(true),
});
exports.RtpayReceiptSettingsSchema = zod_1.z.object({
    text: zod_1.z.boolean().default(true),
    email: zod_1.z.boolean().default(true),
    print: zod_1.z.boolean().default(false),
});
exports.RtpaySettingsSchema = zod_1.z.object({
    displayText: zod_1.z.string().max(60).optional(),
    approvedSeconds: zod_1.z.number().int().min(1).max(30).default(3),
    tip: exports.RtpayTipSettingsSchema.default({}),
    receipt: exports.RtpayReceiptSettingsSchema.default({}),
    thankYouMessage: zod_1.z.string().max(60).optional(),
    returnSeconds: zod_1.z.number().int().min(3).max(60).default(10),
    paymentType: zod_1.z.enum(['Credit']).default('Credit'),
    /** Every terminal screen in one theme. Light by default: DvPayLite's card screen is white. Later follows the POS theme. */
    theme: zod_1.z.enum(['light', 'dark']).default('light'),
});
/** The `rtpay` block on an EMV device row of deviceClass `dejavoo_rubytill_pay`. All optional/additive. */
exports.RtpayDeviceStateSchema = zod_1.z.object({
    mode: exports.RtpayModeSchema.default('charge_only'),
    enabled: zod_1.z.boolean().default(true),
    settings: exports.RtpaySettingsSchema.default({}),
    pinSalt: zod_1.z.string().optional(),
    pinHash: zod_1.z.string().optional(),
    tenantName: zod_1.z.string().optional(),
    locationName: zod_1.z.string().optional(),
    pairedAt: zod_1.z.string().optional(),
    tokenVersion: zod_1.z.number().int().default(1),
    /** Card engine on the device. Absent on every row written before Tap to Pay → DvPayLite. */
    cardEngine: exports.RtpayCardEngineSchema.default('dvpaylite'),
    /** Tap to Pay SDK merchant code (tap_to_pay only). Never sent to a browser in full. */
    merchantCode: zod_1.z.string().max(32).optional(),
});
/** Session status on the relay. Terminal statuses never go back to pending. */
exports.RtpaySaleStatusSchema = zod_1.z.enum(['pending', 'sent', 'on_device', 'approved', 'declined', 'cancelled', 'unknown', 'failed']);
exports.RTPAY_TERMINAL_STATUSES = ['approved', 'declined', 'cancelled', 'unknown', 'failed'];
/** The device's reply, card-holder data already removed on the terminal. */
exports.RtpayResultSchema = zod_1.z.object({
    outcome: zod_1.z.enum(['approved', 'declined', 'cancelled', 'unknown']),
    refId: zod_1.z.string().nullable().optional(),
    message: zod_1.z.string().default(''),
    approvedCents: zod_1.z.number().int().default(0),
    tipCents: zod_1.z.number().int().default(0),
    totalCents: zod_1.z.number().int().default(0),
    feeCents: zod_1.z.number().int().default(0),
    partial: zod_1.z.boolean().default(false),
    authCode: zod_1.z.string().nullable().optional(),
    rrn: zod_1.z.string().nullable().optional(),
    transId: zod_1.z.string().nullable().optional(),
    batchNo: zod_1.z.string().nullable().optional(),
    cardType: zod_1.z.string().nullable().optional(),
    last4: zod_1.z.string().nullable().optional(),
    entryMode: zod_1.z.string().nullable().optional(),
    respCode: zod_1.z.string().nullable().optional(),
    raw: zod_1.z.record(zod_1.z.unknown()).nullable().optional(),
});
/** Relay message types, envelope `{ v: 1, type, ... }`. Additive only. */
exports.RTPAY_PROTOCOL_VERSION = 1;
exports.RtpayServerMessage = { CONFIG: 'config', PAY: 'pay', CANCEL: 'cancel', STATUS: 'status', PING: 'ping', RESULT_ACK: 'result_ack' };
exports.RtpayDeviceMessage = { HELLO: 'hello', ACK: 'ack', PROGRESS: 'progress', RESULT: 'result', RECEIPT: 'receipt', PONG: 'pong' };
//# sourceMappingURL=rubytillPay.js.map